import React, { useState, useEffect } from "react";
import apiRequest from "../../../../../utils/ApiRequest/apiRequest.js";
import LoadingImageElement from "components/Elements/LoadingContainerElement/LoadingImageElement.tsx";
import Pagination from "../../../../Reusable/Pagination/Pagination.js";
import { getDateFormatForInput } from "../../../../../utils/gridHelpers.js";
import { useContent } from "../../../../../contexts/contentContext/contentContext.js";
import Skeleton from "react-loading-skeleton";
import Form from "../../../../Reusable/Form/Form.js";
import { useUser } from "../../../../../contexts/userContext/userContext.js";
import { EMAIL_VALIDATOR } from "../../../../../helpers/validators.js";
import { DynamicEmailInputField } from "../NotificationFields.js";

const PRODUCTION_DATE = new Date("2021-04-21T00:00:00.000Z");

// Поля для формы
const InvoiceFields = {
  ReceiveInvoices: {
    fieldKey: "ReceiveInvoices",
    element: "checkbox",
    title: "ReceiveInvoices",
    label: "profilesettings-receive-invoices",
    className: "general-settings",
    tooltip: {
      text: "profilesettings-receive-invoices-comment",
      icon: "question-circle",
    },
  },
  InvoicesEmail: {
    fieldKey: "InvoicesEmail",
    element: "customelement",
    validation: {
      required: true,
      reg: EMAIL_VALIDATOR,
      invalidMessage: "email-input-invalid-message",
    },
    render: (props) => <DynamicEmailInputField {...props} />,
  },
};

const StatementsPage = ({ userData, setAreStatementsExist }) => {
  const { getTextByKey } = useContent();
  const { getUserData } = useUser();
  const [showTransactions, setShowTransactions] = useState(true);
  const [transactionList, setTransactionList] = useState([]);
  const [globalLoading, setGlobalLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [displayedTransactions, setDisplayedTransactions] = useState([]);
  const [settings, setSettings] = useState({});

  //upper the num of pages
  function up(v, n) {
    return Math.ceil(v * Math.pow(10, n)) / Math.pow(10, n);
  }

  const getListByCustomerExternalIdAsync = async () => {
    const parameters = {
      customerId: userData.UserId,
    };
    const response = await apiRequest(
      "Transactions",
      "GetListByCustomerExternalIdAsync",
      parameters
    );
    if (response.status === "success") {
      if (response.data) {
        setShowTransactions(true);
        let c = response.data.filter((item) => item.Status === "Complete");
        c = c.sort((a, b) => (b.DateTime > a.DateTime ? 1 : -1));
        setTotalTransactions(c.length);
        setTransactionList(c);
        setAreStatementsExist(c.length > 0);
        renderList(c);
      }
      setGlobalLoading(false);
    } else {
      setGlobalLoading(false);
    }
  };

  // Получение настроек пользователя
  const getUserSettings = async () => {
    const response = await apiRequest("Users", "GetUserSettings");
    if (response.status === "success") {
      setSettings(response.data);
    }
  };

  useEffect(() => {
    if (userData) {
      getListByCustomerExternalIdAsync();
      getUserSettings();
    }
  }, []);

  useEffect(() => {
    renderList(transactionList);
  }, [currentPage]);

  const renderList = (list) => {
    if (list && list.length > 0) {
      let tempList = [];
      list
        .slice(currentPage === 1 ? 0 : (currentPage - 1) * 10)
        .map((item, i) => (i < 10 ? tempList.push(item) : null));
      setDisplayedTransactions(tempList);
    }
  };

  const showPrice = (price, currency) => {
    let newPrice = price.toFixed(2);
    let newCurrency = "";
    switch (currency) {
      case "NIS":
        newCurrency = "₪";
        break;
      case "USD":
        newCurrency = "$";
        break;
      case "EUR":
        newCurrency = "€";
        break;
      default: {
        break;
      }
    }
    return (
      <span>
        {newCurrency + " " + newPrice + " "}{" "}
        <span className="currency">{currency}</span>
      </span>
    );
  };

  // Обработчик отправки формы
  const handleFormSubmit = async (body) => {
    const newBody = {
      ...body,
      IsReactPage: true,
      RecivceInvoices: body.ReceiveInvoices,
      InvoicesEmail: body.ReceiveInvoices ? body.InvoicesEmail : "",
    };

    const response = await apiRequest(
      "Users",
      "UpdateUserSettings",
      newBody,
      "POST"
    );

    if (response.status === "success") {
      getUserSettings();
    }

    return response;
  };

  return (
    <div className={`statemetns-table ${showTransactions ? "" : "false"}`}>
      <div className="table-title">
        {!globalLoading ? (
          showTransactions && displayedTransactions.length > 0 ? (
            getTextByKey("payment-method-statement")
          ) : null
        ) : (
          <Skeleton count={1} width={250} height={30} />
        )}
      </div>

      {/* Форма для настроек получения счетов */}
      {!!settings && (
        <div className="invoice-settings-form">
          <Form
            submitProps={{
              title: getTextByKey("profile-settings-update"),
            }}
            fieldsProps={{ label: "hidden" }}
            fields={InvoiceFields}
            externalProps={{
              settings,
            }}
            defaultValuesObj={{
              ReceiveInvoices: settings.RecivceInvoices,
              InvoicesEmail: settings?.InvoicesEmail || getUserData()?.UserName,
            }}
            onSubmit={handleFormSubmit}
          />
        </div>
      )}

      {!globalLoading ? (
        <>
          {showTransactions && displayedTransactions.length > 0 ? (
            <>
              <div className="data-table">
                <div className="header">
                  <div className="invoice-date">
                    {getTextByKey("statement-table-date")}
                  </div>
                  <div className="invoice-id">
                    {getTextByKey("statement-table-invoice")}
                  </div>
                  <div className="invoice-price">
                    {getTextByKey("statement-table-price")}
                  </div>
                  <div className="invoice-url">
                    {getTextByKey("statement-table-pdf")}
                  </div>
                </div>
                <div className="body">
                  {displayedTransactions.length > 0
                    ? displayedTransactions.map((item, i) => (
                        <div key={i} className="body-row">
                          <div className="invoice-date">
                            {getDateFormatForInput(item.DateTime)}
                          </div>
                          <div className="invoice-id">{item.InvoiceId}</div>
                          <div className="invoice-price">
                            {showPrice(item.Price, item.Currency)}
                          </div>
                          <div className="invoice-url">
                            {item.InvoiceURL ? (
                              <a
                                target="_blank"
                                href={item.InvoiceURL}
                                rel="noopener noreferrer"
                              >
                                {getTextByKey("payment-view-pdf")}
                              </a>
                            ) : null}
                          </div>
                        </div>
                      ))
                    : null}
                </div>
              </div>
              {totalTransactions > 10 && (
                <Pagination
                  maxPages={
                    totalTransactions > 0 ? up(totalTransactions / 10, 0) : 1
                  }
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                />
              )}
            </>
          ) : null}
        </>
      ) : (
        <div className="statement-loading">
          <LoadingImageElement />
        </div>
      )}
    </div>
  );
};

export default StatementsPage;
